/**
 * Find Panel — the choice panel behind the "Let's Find Your Furniture" calls to action.
 *
 * Rendered by sections/find-panel.liquid. Two jobs beyond what DialogComponent already does:
 *
 *   1. Turn ordinary links into triggers. Any anchor whose URL ends in the section's trigger
 *      fragment (default "#find-furniture") opens the panel instead of navigating, so every
 *      call to action stays a plain `url` setting the client can point anywhere — and a
 *      shopper without JavaScript still lands on the page that link names.
 *   2. Scroll to an option that points at a section of the page already on screen, rather than
 *      reloading it. "Browse Furniture" links to "/#shop-by-room", so it navigates home from a
 *      product page but simply glides down to Shop by Room when the shopper is already there.
 *   3. Swipe down to dismiss on touch devices.
 *
 * Escape, the close button and the open/close animation come from DialogComponent; the scroll
 * lock comes from the `scroll-lock` attribute on the dialog. Click-outside is re-implemented
 * here because the dialog element deliberately fills the viewport (the visible card is the
 * sheet inside it), so the base class's check against the dialog's own box never matches.
 */

import { DialogComponent } from '@theme/dialog';
import { isClickedOutside, isMobileBreakpoint } from '@theme/utilities';

/** Pixels the sheet must travel before a drag counts as a dismiss. */
const DISMISS_DISTANCE = 96;

/** Pixels per millisecond at which a short flick dismisses regardless of distance. */
const DISMISS_VELOCITY = 0.5;

/** Pixels of movement before a touch is treated as a drag rather than a tap or a scroll. */
const DRAG_THRESHOLD = 6;

/** Milliseconds the sheet takes to finish sliding out after a dismiss. */
const DISMISS_DURATION = 200;

const DEFAULT_FRAGMENT = 'find-furniture';

/**
 * Milliseconds to let the dialog finish closing before scrolling. The scroll lock is released
 * on close, and scrolling the body while it is still locked goes nowhere.
 */
const SCROLL_DELAY = 60;

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} sheet - The visible card inside the dialog.
 *
 * @extends DialogComponent
 */
export class FindPanel extends DialogComponent {
  requiredRefs = ['dialog', 'sheet'];

  /** @type {{ pointerId: number, startY: number, startTime: number, offset: number, active: boolean } | null} */
  #drag = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #dismissTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #scrollTimer;

  connectedCallback() {
    super.connectedCallback();

    const { sheet } = this.refs;

    document.addEventListener('click', this.#handleTriggerClick);
    window.addEventListener('hashchange', this.#openFromLocation);
    this.addEventListener('click', this.#handleOptionClick);
    this.addEventListener('click', this.#handleBackdropClick);
    this.addEventListener('dialog:close', this.#resetSheet);
    sheet.addEventListener('pointerdown', this.#handlePointerDown);
    sheet.addEventListener('pointermove', this.#handlePointerMove);
    sheet.addEventListener('pointerup', this.#handlePointerUp);
    sheet.addEventListener('pointercancel', this.#handlePointerUp);

    this.#openFromLocation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const sheet = this.refs?.sheet;

    document.removeEventListener('click', this.#handleTriggerClick);
    window.removeEventListener('hashchange', this.#openFromLocation);
    this.removeEventListener('click', this.#handleOptionClick);
    this.removeEventListener('click', this.#handleBackdropClick);
    this.removeEventListener('dialog:close', this.#resetSheet);
    sheet?.removeEventListener('pointerdown', this.#handlePointerDown);
    sheet?.removeEventListener('pointermove', this.#handlePointerMove);
    sheet?.removeEventListener('pointerup', this.#handlePointerUp);
    sheet?.removeEventListener('pointercancel', this.#handlePointerUp);

    clearTimeout(this.#dismissTimer);
    clearTimeout(this.#scrollTimer);
    this.#drag = null;
  }

  /**
   * The URL fragment that opens this panel, including the leading "#".
   *
   * @returns {string} The fragment, e.g. "#find-furniture".
   */
  get triggerFragment() {
    const value = (this.dataset.triggerFragment || DEFAULT_FRAGMENT).trim().replace(/^#+/, '');

    return value ? `#${value}` : `#${DEFAULT_FRAGMENT}`;
  }

  /**
   * Opens the panel when a link pointing at the trigger fragment is clicked.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleTriggerClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    // Let the shopper open the fallback page in a new tab if they ask for it.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target;
    const link = target instanceof Element ? target.closest('a[href]') : null;

    if (!(link instanceof HTMLAnchorElement) || link.target === '_blank') return;
    if (this.#hashOf(link.href) !== this.triggerFragment) return;

    event.preventDefault();
    this.showDialog();
  };

  /**
   * Turns an option that points at a section of the current page into a scroll.
   *
   * Without this the browser would reload the page just to land on an anchor it is already
   * showing, and the shopper would watch the whole homepage repaint. Anything that leaves the
   * page — a different path, a phone number, a new tab — is left to the browser untouched.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleOptionClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target;
    const link = target instanceof Element ? target.closest('a[href]') : null;

    if (!(link instanceof HTMLAnchorElement) || link.target === '_blank') return;

    const anchored = this.#samePageAnchor(link.href);

    if (!anchored) return;

    event.preventDefault();
    this.closeDialog();

    clearTimeout(this.#scrollTimer);
    this.#scrollTimer = setTimeout(() => this.#scrollTo(anchored), SCROLL_DELAY);
  };

  /**
   * The element an href points at, but only when that href names a fragment of the page the
   * shopper is already on and the fragment resolves to something real.
   *
   * @param {string} href - The URL to resolve.
   * @returns {Element | null} The element to scroll to, or null.
   */
  #samePageAnchor(href) {
    let url;

    try {
      url = new URL(href, window.location.href);
    } catch {
      return null;
    }

    if (url.origin !== window.location.origin) return null;
    if (url.pathname !== window.location.pathname) return null;
    if (url.hash.length < 2) return null;
    if (url.hash === this.triggerFragment) return null;

    try {
      return document.querySelector(url.hash);
    } catch {
      return null;
    }
  }

  /**
   * @param {Element} element - The element to bring into view.
   */
  #scrollTo(element) {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    element.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }

  /**
   * Opens the panel when the page is loaded at, or navigated to, the trigger fragment.
   * Keeps a shared "/collections#find-furniture" link behaving like the button does.
   */
  #openFromLocation = () => {
    if (window.location.hash !== this.triggerFragment) return;

    this.showDialog();
  };

  /**
   * Reads the fragment of a URL without throwing on a malformed one.
   *
   * @param {string} href - The URL to read.
   * @returns {string} The fragment, or an empty string.
   */
  #hashOf(href) {
    try {
      return new URL(href, window.location.href).hash;
    } catch {
      return '';
    }
  }

  /**
   * Closes the panel when the shopper taps beside the sheet.
   *
   * The dialog fills the viewport, so this checks against the sheet rather than the dialog.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleBackdropClick = (event) => {
    const { dialog, sheet } = this.refs;

    if (!dialog.open || this.#drag) return;
    if (!isClickedOutside(event, sheet)) return;

    this.closeDialog();
  };

  /**
   * @param {PointerEvent} event - The pointer event.
   */
  #handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' || !isMobileBreakpoint()) return;
    if (!this.#canStartDrag(event)) return;

    this.#drag = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startTime: event.timeStamp,
      offset: 0,
      active: false,
    };
  };

  /**
   * A drag may only begin on the grabber, or while the sheet is scrolled to the top —
   * otherwise it would steal the gesture from the sheet's own scrolling.
   *
   * @param {PointerEvent} event - The pointer event.
   * @returns {boolean} Whether a drag may begin.
   */
  #canStartDrag(event) {
    const { sheet } = this.refs;
    const target = event.target;

    if (target instanceof Element && target.closest('.find-panel__grabber')) return true;

    return sheet.scrollTop <= 0;
  }

  /**
   * @param {PointerEvent} event - The pointer event.
   */
  #handlePointerMove = (event) => {
    const drag = this.#drag;

    if (!drag || event.pointerId !== drag.pointerId) return;

    const { sheet } = this.refs;
    const offset = event.clientY - drag.startY;

    if (!drag.active) {
      // Upward or negligible movement is a scroll or a tap, not a dismiss.
      if (offset < DRAG_THRESHOLD) return;

      drag.active = true;
      sheet.setPointerCapture(event.pointerId);
      sheet.style.transition = 'none';
    }

    drag.offset = Math.max(0, offset);
    sheet.style.transform = `translateY(${drag.offset}px)`;
  };

  /**
   * @param {PointerEvent} event - The pointer event.
   */
  #handlePointerUp = (event) => {
    const drag = this.#drag;

    if (!drag || event.pointerId !== drag.pointerId) return;

    const { sheet } = this.refs;

    this.#drag = null;

    if (sheet.hasPointerCapture(event.pointerId)) sheet.releasePointerCapture(event.pointerId);
    if (!drag.active) return;

    const elapsed = Math.max(1, event.timeStamp - drag.startTime);
    const velocity = drag.offset / elapsed;

    if (drag.offset >= DISMISS_DISTANCE || velocity >= DISMISS_VELOCITY) {
      this.#dismiss();
      return;
    }

    this.#glideSheetTo('translateY(0)');
  };

  /**
   * Slides the sheet the rest of the way out, then closes. Doing it in that order leaves the
   * dialog's own closing animation with nothing but the backdrop left to fade.
   */
  #dismiss() {
    this.#glideSheetTo('translateY(100%)');

    clearTimeout(this.#dismissTimer);
    this.#dismissTimer = setTimeout(() => this.closeDialog(), DISMISS_DURATION);
  }

  /**
   * @param {string} transform - The transform to animate the sheet to.
   */
  #glideSheetTo(transform) {
    const { sheet } = this.refs;

    sheet.style.transition = `transform ${DISMISS_DURATION}ms var(--animation-easing, ease-in-out)`;
    sheet.style.transform = transform;
  }

  #resetSheet = () => {
    const { sheet } = this.refs;

    clearTimeout(this.#dismissTimer);
    sheet.style.transition = '';
    sheet.style.transform = '';
  };
}

if (!customElements.get('find-panel')) customElements.define('find-panel', FindPanel);
